import {useSyncExternalStore} from "react";
import type {StorageMap} from "../storage";
import {storageWrapper} from "../storage";
import {ExternalStore} from "./ExternalStore";

type Opts<TKey, TStoreKey> = {
  keyToStorageKey: (key: TKey) => TStoreKey;
};

// One localStorage entry. It picks up other tabs' writes for the app's lifetime, which is fine
// for the handful of keys (one per group) this is used with.
class StoredValue<TStoreKey extends keyof StorageMap> extends ExternalStore<
  StorageMap[TStoreKey] | null
> {
  private value: StorageMap[TStoreKey] | null;

  constructor(private storageKey: TStoreKey) {
    super();
    this.value = storageWrapper.storageGet(storageKey);
    window.addEventListener("storage", (e) => {
      // `key` is null when another tab clears the storage
      if (e.key !== storageKey && e.key !== null) return;
      this.value = storageWrapper.storageGet(storageKey);
      this.notify();
    });
  }

  getSnapshot = () => this.value;

  set(value: StorageMap[TStoreKey]) {
    storageWrapper.storageSet(this.storageKey, value);
    this.value = value;
    this.notify();
  }
}

export const createReactiveLocalStoreByKey = <TKey, TStoreKey extends keyof StorageMap>({
  keyToStorageKey,
}: Opts<TKey, TStoreKey>) => {
  type TValue = StorageMap[TStoreKey];
  const storedByKey = new Map<TKey, StoredValue<TStoreKey>>();
  const getStored = (key: TKey) => {
    let stored = storedByKey.get(key);
    if (!stored) {
      stored = new StoredValue(keyToStorageKey(key));
      storedByKey.set(key, stored);
    }
    return stored;
  };
  const useValue = (key: TKey) => {
    const stored = getStored(key);
    return useSyncExternalStore(stored.subscribe, stored.getSnapshot);
  };
  const setValue = (key: TKey, value: TValue) => getStored(key).set(value);
  const getValue = (key: TKey) => getStored(key).getSnapshot();

  return [useValue, setValue, {getValue}] as const;
};
